import styles from "src/styles/agGridCustom.module.scss";
import * as Validators from "src/utils/validators";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { store } from "src/index";
import {
    CUSTODY_ASSET_TYPE_KEYS,
    CUSTODY_ASSET_TYPES,
} from "src/redux/Confirmations/constants";
import DropdownItemRenderer from "src/components/common/AgGrid/DropdownItemRenderer";
import isEmpty from "is-empty";

export const fundDefaultColDef = {
    resizable: false,
    sortable: false,
    filter: false,
};

const valueFormatter = (params) => {
    if (
        params.colDef.colId.startsWith("Create") &&
        !params.data.hasOwnProperty(params.colDef.field)
    )
        return "";

    if (["ksdAccount", "ksfcAccount"].includes(params.colDef.field)) {
        return isEmpty(params.value)
            ? "-"
            : `${params.value.name}-${params.value.accountNo}`;
    }

    return params.value || "-";
};

const cellClass = () => {
    return [styles.agCenterAlign];
};

const getNewValueCellError = (params) => {
    const displayError = SettingsSelectors.DisplayFundErrors(store.getState());
    if (!displayError) return;
    const currentFundSettings = SettingsSelectors.FundSettings(
        store.getState()
    );
    const { "enable-multi-account-code-support": isAccountSelectable } =
        SettingsSelectors.FeatureFlags(store.getState());
    const errors = Validators.getFundValidationErrors(
        params.data,
        currentFundSettings,
        isAccountSelectable
    );
    return (
        errors && errors.some((error) => error.startsWith(params.colDef.field))
    );
};

const cellClassRules = {
    [styles.agNewValueCellError]: (params) => getNewValueCellError(params),
    [styles.agEditableBlack]: (params) => !getNewValueCellError(params),
};

export const getCreateFundColumnDefs = ({ isAccountSelectable }) => {
    let columns = [
        {
            headerName: "Fund Name",
            colId: "Create Fund Name",
            field: "fundName",
            width: 152,
            minWidth: 152,
            editable: true,
            cellClassRules,
            cellClass,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Internal Fund Code",
            colId: "Create Internal Fund Code",
            field: "internalFundCode",
            width: 88,
            minWidth: 88,
            editable: true,
            cellClass,
            cellClassRules,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        isAccountSelectable
            ? {
                  headerName: "KSD Account",
                  colId: "Create KSD Account",
                  field: "ksdAccount",
                  width: 150,
                  minWidth: 150,
                  editable: true,
                  cellClassRules,
                  cellClass,
                  headerClass: styles.agHeaderCenterAlign,
                  menuTabs: [],
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: (params) => ({
                      values: [
                          {},
                          ...params.context.accounts.filter(
                              (a) => a.type === "KSD"
                          ),
                      ],
                      formatValue: (value) =>
                          isEmpty(value)
                              ? "-"
                              : `${value.name}-${value.accountNo}`,
                      cellRenderer: DropdownItemRenderer,
                  }),
                  valueFormatter,
              }
            : {},
        {
            headerName: "SLB Num\n(대차계정번호)",
            colId: "Create SLB Number",
            field: "slbNumber",
            width: 96,
            minWidth: 96,
            editable: true,
            cellClass,
            cellClassRules,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "KSD Fund Code\n(예탁원 펀드 코드)",
            colId: "Create KSD Fund Code",
            field: "ksdFundCode",
            width: 120,
            minWidth: 120,
            editable: true,
            cellClass,
            cellClassRules,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        isAccountSelectable
            ? {
                  headerName: "KSFC Account",
                  colId: "Create KSFC Account",
                  field: "ksfcAccount",
                  width: 150,
                  minWidth: 150,
                  editable: true,
                  cellClassRules,
                  cellClass,
                  headerClass: styles.agHeaderCenterAlign,
                  menuTabs: [],
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: (params) => ({
                      values: [
                          {},
                          ...params.context.accounts.filter(
                              (a) => a.type === "KSFC"
                          ),
                      ],
                      formatValue: (value) =>
                          isEmpty(value)
                              ? "-"
                              : `${value.name}-${value.accountNo}`,
                      cellRenderer: DropdownItemRenderer,
                  }),
                  valueFormatter,
              }
            : {},
        {
            headerName: "KSFC Fund Code\n(한국증권금융 펀드 코드)",
            colId: "Create KSFC Fund Code",
            field: "ksfcFundCode",
            width: 144,
            minWidth: 144,
            editable: true,
            cellClass,
            cellClassRules,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Custody Asset Type\n(예탁재산구분 코드)",
            colId: "Create Custody Asset Type",
            field: "custodyAssetType",
            width: 155,
            minWidth: 155,
            editable: true,
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: {
                values: CUSTODY_ASSET_TYPE_KEYS,
                formatValue: (value) => CUSTODY_ASSET_TYPES[value] || "-",
                cellRenderer: DropdownItemRenderer,
            },
            valueFormatter,
            cellClassRules,
            cellClass,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
    ];
    return columns.filter((col) => !isEmpty(col));
};

export const getDetailFundColumnDefs = ({ isAccountSelectable }) => {
    let columns = [
        {
            headerName: "Fund Name",
            colId: "Details Fund Name",
            field: "fundName",
            width: 140,
            minWidth: 140,
            cellClass: [styles.agCenterAlign, styles.agConfDetail],
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Internal\nFund Code",
            colId: "Details Internal Fund Code",
            field: "internalFundCode",
            width: 103,
            minWidth: 103,
            cellClass: [styles.agCenterAlign, styles.agConfDetail],
            valueFormatter,
            menuTabs: [],
        },
        isAccountSelectable
            ? {
                  headerName: "KSD Account",
                  colId: "Details KSD Account",
                  field: "ksdAccount",
                  width: 150,
                  minWidth: 150,
                  cellClass: [styles.agCenterAlign, styles.agConfDetail],
                  valueFormatter,
                  headerClass: styles.agHeaderCenterAlign,
                  menuTabs: [],
              }
            : {},
        {
            headerName: "SLB Number",
            colId: "Details SLB Number",
            field: "slbNumber",
            width: 88,
            minWidth: 88,
            cellClass: [styles.agCenterAlign, styles.agConfDetail],
            valueFormatter,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "KSD Fund Code",
            colId: "Details KSD Fund Code",
            field: "ksdFundCode",
            width: 130,
            minWidth: 130,
            cellClass: [styles.agCenterAlign, styles.agConfDetail],
            valueFormatter,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        isAccountSelectable
            ? {
                  headerName: "KSFC Account",
                  colId: "Details KSFC Account",
                  field: "ksfcAccount",
                  width: 150,
                  minWidth: 150,
                  cellClass: [styles.agCenterAlign, styles.agListDetail],
                  valueFormatter,
                  headerClass: styles.agHeaderCenterAlign,
                  menuTabs: [],
              }
            : {},
        {
            headerName: "KSFC Fund Code",
            colId: "Details KSFC Fund Code",
            field: "ksfcFundCode",
            width: 120,
            minWidth: 120,
            cellClass: [styles.agCenterAlign, styles.agConfDetail],
            valueFormatter,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Custody Asset Type",
            colId: "Details Custody Asset Type",
            field: "custodyAssetType",
            width: 120,
            minWidth: 120,
            cellClass: [styles.agCenterAlign, styles.agConfDetail],
            valueFormatter,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
        {
            headerName: "Delete",
            colId: "Details Delete",
            cellRenderer: "deleteButton",
            width: 54,
            minWidth: 54,
            cellClass: [styles.agCenterAlign, styles.agConfDetail],
            valueFormatter,
            headerClass: styles.agHeaderCenterAlign,
            menuTabs: [],
        },
    ];
    return columns.filter((col) => !isEmpty(col));
};
